<script setup lang="ts">
import { IconArrowRight, IconReload } from '@/components/icons'
import { useElements } from '@/stores/elements'
import { useStore } from '@/stores/store'
import { storeToRefs } from 'pinia'
import { computed, onMounted, watch } from 'vue'

const store = useStore()
const elementsStore = useElements()
const { elements } = storeToRefs(elementsStore)
const {
  shapes,
  shape,
  cushion,
  edgeSewing,
  currentStep,
  diameter_a_circle,
  width_a_rectangle,
  length_b_rectangle,
  isButtonMiddle,
  isButtons,
  fabrics,
  topStitchesType,
  imgPath,
} = storeToRefs(store)
const isNextVisible = computed(() => Boolean(cushion.value && shape.value && edgeSewing.value))
const isSomeSelected = computed(() => Boolean(cushion.value || shape.value || edgeSewing.value))
const formPath = store.imgPath + '/form/'
const optionMappings = {
  cushionType: new Map([
    ['flat_cushion', 1],
    ['box_cushion', 2],
  ]),
  buttons: new Map([
    ['buttons_yes', 1],
    ['buttons_no', 2],
  ]),
  stitches: new Map([
    ['stitches_yes', 1],
    ['stitches_no', 2],
  ]),
  edgeSewing: new Map([
    ['flat_edge', 1],
    ['double_edge', 2],
    ['pipe_edge', 3],
  ]),
  topStitches: new Map([
    ['top_stitches_single', 2],
    ['top_stitches_double', 3],
  ]),
}

const materialMapping = {
  '2': 'Re•Studio Black',
  '3': 'Re•Studio cognac',
  '4': 'Re•Studio Grey',
  '5': 'Re•Studio Ice',
  '6': 'Re•Studio Nougat',
  '7': 'Re•Studio black anilin',
  '8': 'Re•Studio taupe Anilin',
  '9': 'Re•Studio marble anilin',
  '10': 'Re•Studio espresso anilin',
  '11': 'Re•Studio Teak Anilin',
  '12': 'Re•Studio Bronze Anilin',
  '13': 'Re•Studio Nougat Anilin',
  '14': 'Re•Studio Cognac Anilin',
  '15': 'Re•Studio Nougat Anilin',
  '16': 'Re•Studio Sand Anilin',
  '17': 'Re•Studio Savanne Anilin',
  '18': 'Re•Studio LightOlive Anilin',
  '19': 'Re•Studio Peat Anilin',
  '20': 'Re•Studio Reed Anilin',
  '21': 'Re•Studio Sahara Anilin',
  '22': 'Re•Studio Cherry Anilin',
  '23': 'Re•Studio Indigoblue Anilin',
  '24': 'Re•Studio Lavender Anilin',
  '25': 'Re•Studio Fango Anilin',
  '26': 'Re•Studio Mint Anilin',
  '27': 'Re•Studio Porcelain Anilin',
  '28': 'Re•Studio 92 Indigo',
  '29': 'Re•Studio 93 Aqua',
  '30': 'Re•Studio 95 Mineral',
  '31': 'Re•Studio 96 Bright Green',
  '32': 'Re•Studio 98 Glass',
  '33': 'Re•Studio 99 Crystal',
  '34': 'Re•Studio 100 Lagoon',
  '35': 'Re•Studio 102 Pacific',
  '36': 'Re•Studio 103 Sky',
  '37': 'Re•Studio 104 Night',
  '38': 'Re•Studio 106 midnight',
  '39': 'Re•Studio 126 Onyx',
  '40': 'Re•Studio 128 Typhoon',
  '41': 'Re•Studio 129 Savile Grey',
  '42': 'Re•Studio 130 Pelican',
  '43': 'Re•Studio 131 Quartz',
  '44': 'Re•Studio 132 Toned',
  '45': 'Re•Studio 133 Ice White',
  '46': 'Re•Studio 134 Antique Green',
  '47': 'Re•Studio 135 Cypress',
  '48': 'Re•Studio 136 Power Blue',
  '49': 'Re•Studio 137 Wave',
  '50': 'Re•Studio 138 Pool',
  '51': 'Re•Studio 140 Storm',
  '52': 'Re•Studio 141 Cyan',
  '53': 'Re•Studio 142 Gale',
  '54': 'Re•Studio 143 Mint',
  '55': 'Re•Studio 144 Thistle',
  '56': 'Re•Studio 145 Nocturne',
  '57': 'Re•Studio 148 Orchid',
  '58': 'Re•Studio 150 Rosé',
  '59': 'Re•Studio 151 Ruby',
  '60': 'Re•Studio 152 Brick',
  '61': 'Re•Studio 154 Mustard',
  '62': 'Re•Studio 155 Lemongrass',
  '63': 'Re•Studio 156 Onion',
  '64': 'Re•Studio 157 Fudge',
  '65': 'Re•Studio 158 Broken',
  '66': 'Re•Studio 161 Soft Sand',
  '67': 'Nordisktopdesign stof',
}

function getOptionValue(option: string, mapping: Map<string, number>) {
  return mapping.get(option) || 1
}

function getMaterialOptionValue(option: string | never[] | undefined) {
  for (const material in materialMapping) {
    // @ts-expect-error nevermind this typescript behavior
    if (option && option.includes(materialMapping[material])) {
      return material
    }
  }
  return '1'
}

function getCushionImage(code: string) {
  const typeOfCushion = getOptionValue(code, optionMappings.cushionType)
  const buttons = getOptionValue(isButtons.value, optionMappings.buttons)
  const edgeSewingValue = getOptionValue(edgeSewing.value, optionMappings.edgeSewing)
  const topStitches = getOptionValue(topStitchesType.value, optionMappings.topStitches)
  const material = getMaterialOptionValue(fabrics?.value?.data?.name)
  if (import.meta.env.PROD) {
    return `${window.renderpath}${typeOfCushion}_${buttons}_${edgeSewingValue}_${topStitches}_${material}.webp`
  }
  return `${imgPath.value}/renders/${typeOfCushion}_${buttons}_${edgeSewingValue}_${topStitches}_${material}.webp`
}

function getEdgeSewingImage(code: string) {
  const typeOfCushion = getOptionValue(cushion.value, optionMappings.cushionType)
  const buttons = getOptionValue(isButtons.value, optionMappings.buttons)
  const edgeSewingValue = getOptionValue(code, optionMappings.edgeSewing)
  const topStitches = getOptionValue(topStitchesType.value, optionMappings.topStitches)
  const material = getMaterialOptionValue(fabrics?.value?.data?.name)
  if (import.meta.env.PROD) {
    return `${window.renderpath}${typeOfCushion}_${buttons}_${edgeSewingValue}_${topStitches}_${material}.webp`
  }
  return `${imgPath.value}/renders/${typeOfCushion}_${buttons}_${edgeSewingValue}_${topStitches}_${material}.webp`
}

watch(shape, (newShape) => {
  if (newShape === 'rectangle') {
    diameter_a_circle.value = ''
    isButtonMiddle.value = ''
  }
  if (newShape == 'circle') {
    width_a_rectangle.value = ''
    length_b_rectangle.value = ''
  }
})
const types = [
  {
    name: 'Box cushion',
    type: 'box_cushion',
  },
  {
    name: 'Flat cushion',
    type: 'flat_cushion',
  },
]

const edgeSewings = [
  {
    name: 'Flat',
    type: 'flat_edge',
  },
  {
    name: 'Double',
    type: 'double_edge',
  },
  {
    name: 'Pipe',
    type: 'pipe_edge',
  },
]

function isEditCushion() {
  return window.location.href.indexOf('checkout/cart/configure/id') > 0
}

onMounted(() => {
  elements.value = {
    shape: {
      rectangle: document.querySelector('input[data-sku="rectangle"]'),
      circle: document.querySelector('input[data-sku="circle"]'),
    },
    thickness: document.querySelectorAll('input[data-sku^="thick_"]'),
    buttonsCols: document.querySelector('input[data-sku="buttons_cols"]'),
    buttonsRows: document.querySelector('input[data-sku="buttons_rows"]'),
    buttonsDistanceToEdge: document.querySelector('input[data-sku="buttons_edge_distance"]'),
    stitchesDistanceToEdge: document.querySelector('input[data-sku="stitches_edge_distance"]'),
    stitchesCols: document.querySelector('input[data-sku="stitches_rows"]'),
    stitchesRows: document.querySelector('input[data-sku="stitches_cols"]'),
    width_a_rectangle: document.querySelector('input[data-sku="width_a_rectangle"]'),
    length_b_rectangle: document.querySelector('input[data-sku="length_b_rectangle"]'),
    diameter_a_circle: document.querySelector('input[data-sku="diameter_a_circle"]'),
  }

  if (isEditCushion()) {
    // for (const type in elements.value.shape) {
    //   //@ts-expect-error deep types works wrong
    //   const shapeElement = elements.value.shape[type]
    //   if (shapeElement && shapeElement.checked) {
    //     handleShapeSelection(type, elements.value)
    //   }
    // }
  }
})

function toNext() {
  currentStep.value = 2
  document.querySelector('.configurator_right_column')?.scrollIntoView({ behavior: 'smooth' })
}
</script>
<template>
  <div id="first-page" class="first-page-wrapper" v-if="currentStep === 1">
    <div>
      <div class="content-wrapper space-y-10">
        <fieldset class="shapes">
          <legend class="text-lg lg:text-2xl font-semibold">{{ $t('Choose shape') }}</legend>

          <div class="shape-wrapper">
            <label v-for="item in shapes" :key="item.type" class="shape-item" :for="item.type">
              <div class="shape-wrapper-inner">
                <img
                  class="object-cover w-40"
                  :src="formPath + item.type + '.webp'"
                  :alt="item.name"
                />
              </div>
              <div class="flex items-center gap-2 mt-4">
                <input
                  :id="item.type"
                  :key="item.type"
                  v-model="shape"
                  type="radio"
                  :value="item.type"
                  name="shape"
                  class="radio-input"
                />
                <span class="shape-label">{{ $t(item.name) }}</span>
              </div>
            </label>
          </div>
        </fieldset>
        <fieldset class="types">
          <legend class="text-lg lg:text-2xl font-semibold">{{ $t('Type') }}</legend>

          <div class="shape-wrapper">
            <label v-for="item in types" :key="item.type" class="shape-item" :for="item.type">
              <div class="shape-wrapper-inner">
                <img class="object-cover w-40" :src="getCushionImage(item.type)" :alt="item.name" />
              </div>
              <div class="flex items-center gap-2 mt-4">
                <input
                  :id="item.type"
                  :key="item.type"
                  v-model="cushion"
                  type="radio"
                  :value="item.type"
                  name="cushion"
                  class="radio-input"
                />

                <span class="shape-label">{{ $t(item.name) }}</span>
              </div>
            </label>
          </div>
        </fieldset>
        <fieldset class="edge-sewings">
          <legend class="text-lg lg:text-2xl font-semibold">{{ $t('Sewings') }}</legend>

          <div class="shape-wrapper">
            <label v-for="item in edgeSewings" :key="item.type" class="shape-item" :for="item.type">
              <div class="shape-wrapper-inner">
                <img
                  class="object-cover w-40"
                  :src="getEdgeSewingImage(item.type)"
                  :alt="item.name"
                />
              </div>
              <div class="flex items-center gap-2 mt-4">
                <input
                  :id="item.type"
                  :key="item.type"
                  v-model="edgeSewing"
                  type="radio"
                  :value="item.type"
                  name="edgeSewing"
                  class="radio-input"
                />
                <span class="shape-label">{{ $t(item.name) }}</span>
              </div>
            </label>
          </div>
        </fieldset>
        <span class="text-black/60 inline-block">{{ $t('*The images are indicative') }}</span>
        <div class="flex flex-col lg:flex-row justify-between gap-4">
          <button
            :disabled="!isNextVisible"
            type="button"
            class="btn btn-primary lg:w-auto"
            @click="toNext"
          >
            {{ $t('Next') }}
            <IconArrowRight />
          </button>
          <button v-show="isSomeSelected" type="button" class="btn" @click="store.nullForm">
            <IconReload />
            <span>{{ $t('Drop off') }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
<style scoped>
.shapes-list {
  display: flex;
  gap: 16px;
}
</style>
